import type { ComplianceState } from '../configuration-compliance/useComplianceData';
import type {
  WazuhComplianceAgentSummaryData,
  WazuhComplianceControlItem,
} from 'src/types/mindtime/wazuhCompliance';
import { previewPaged } from './previewUtils';

export const PREVIEW_COMPLIANCE_IDS = ['preview-compliance-1', 'preview-compliance-2'] as const;

const PREVIEW_CONTROLS: WazuhComplianceControlItem[] = [
  {
    id: 'preview-compliance-1',
    checkId: 'CIS-2.2.1',
    title: 'Ensure password history is configured',
    framework: ['CIS', 'NIST'],
    category: 'Access Control',
    status: 'failed',
    result: 'failed',
    endpoint: 'FIN-SRV-02',
    organization: 'Acme Financial',
    lastChecked: '2026-08-28T08:00:00.000Z',
    description: 'Password history should be set to at least 24 passwords.',
    hostName: 'fin-srv-02.acme.local',
  },
  {
    id: 'preview-compliance-2',
    checkId: 'CIS-5.1.2',
    title: 'Ensure SSH root login is disabled',
    framework: ['CIS'],
    category: 'Network',
    status: 'passed',
    result: 'passed',
    endpoint: 'APP-SERVER-01',
    organization: 'Nexus Dynamics',
    lastChecked: '2026-08-28T07:30:00.000Z',
    hostName: 'app-server-01.nexus.local',
  },
  {
    id: 'preview-compliance-3',
    checkId: 'PCI-8.2.3',
    title: 'Password minimum length requirements',
    framework: ['PCI DSS'],
    category: 'Authentication',
    status: 'failed',
    result: 'failed',
    endpoint: 'HR-PC-17',
    organization: 'Vertex Solutions',
    lastChecked: '2026-08-27T16:45:00.000Z',
    hostName: 'hr-pc-17.vertex.local',
  },
];

export const getCompliancePreviewState = (): ComplianceState => ({
  loading: false,
  summary: {
    overallComplianceScore: 74,
    compliantEndpoints: 16,
    nonCompliantEndpoints: 8,
    failedSecurityChecks: 23,
  },
  controls: previewPaged(PREVIEW_CONTROLS, 0, 15),
  endpoints: previewPaged(
    [
      {
        systemWazuhAgentId: 'preview-endpoint-2',
        endpoint: 'HR-PC-17',
        hostName: 'hr-pc-17.vertex.local',
        organization: 'Vertex Solutions',
        complianceScore: 62,
        failedChecks: 8,
        passedChecks: 42,
      },
      {
        systemWazuhAgentId: 'preview-endpoint-1',
        endpoint: 'FIN-SRV-02',
        hostName: 'fin-srv-02.acme.local',
        organization: 'Acme Financial',
        complianceScore: 81,
        failedChecks: 3,
        passedChecks: 55,
      },
    ],
    0,
    10,
  ),
});

export const getComplianceDetailPreview = (id: string): WazuhComplianceControlItem | null =>
  PREVIEW_CONTROLS.find((row) => row.id === id) ?? PREVIEW_CONTROLS[0] ?? null;

export const getAgentCompliancePreview = (
  endpointLabel: string,
): { summary: WazuhComplianceAgentSummaryData; items: WazuhComplianceControlItem[] } => {
  const label = endpointLabel.trim();
  const items = PREVIEW_CONTROLS.filter(
    (row) => !label || row.endpoint?.toLowerCase() === label.toLowerCase(),
  );
  const list = items.length ? items : PREVIEW_CONTROLS.slice(0, 2);
  const failed = list.filter((row) => row.status === 'failed').length;
  const passed = list.filter((row) => row.status === 'passed').length;

  return {
    summary: {
      passedControls: passed,
      failedControls: failed,
      notApplicable: 0,
      complianceScore: passed + failed > 0 ? Math.round((passed / (passed + failed)) * 100) : 74,
      meta: { lastSyncedAt: '2026-08-28T10:00:00.000Z' },
    },
    items: list,
  };
};
